import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, Loader2 } from 'lucide-react';
import AdminLayout from '@/components/layout/AdminLayout';
import apiService from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { useAdminConfig } from '@/hooks/useAdminConfig';
import { responsive, componentStyles, layouts, typography } from '@/utils/responsiveDesign';

interface DashboardStats {
  activeScale: number;
  awaitingReview: number;
  variationRequests: number;
  overdueActions: number;
  complianceWarnings: number;
  openJobs: number;
}

const AdminDashboardPage = () => {
  const { config } = useAdminConfig();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [adminStaff, setAdminStaff] = useState<{ id: number; name: string; email: string; role: string }[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    activeScale: 0,
    awaitingReview: 0,
    variationRequests: 0,
    overdueActions: 0,
    complianceWarnings: 0,
    openJobs: 0,
  });

  // Create Discount Modal
  const [showCreateDiscountModal, setShowCreateDiscountModal] = useState(false);
  const [discountFormData, setDiscountFormData] = useState({
    discountCode: '',
    internalTitle: '',
    discountType: config.forms.defaultDiscountType,
    discountValue: ''
  });

  // Invite Admin Modal
  const [showInviteAdminModal, setShowInviteAdminModal] = useState(false);
  const [adminFormData, setAdminFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    role: config.forms.defaultAdminRole,
    sendImmediately: true
  });

  // Change Admin Role Modal
  const [showChangeAdminRoleModal, setShowChangeAdminRoleModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<any>(null);
  const [roleChangeData, setRoleChangeData] = useState({
    newRole: config.forms.defaultAdminRole,
    addedCapabilities: [] as string[],
    removedAccess: [] as string[],
    justification: '',
    confirmed: false
  });

  // Suspend Admin Account Modal
  const [showSuspendAdminModal, setShowSuspendAdminModal] = useState(false);
  const [suspendFormData, setSuspendFormData] = useState({
    suspensionReason: config.forms.defaultSuspensionReason,
    auditNotes: ''
  });
  const [selectedAdminToSuspend, setSelectedAdminToSuspend] = useState<any>(null);

  // Resolve Case Modal
  const [showResolveCaseModal, setShowResolveCaseModal] = useState(false);
  const [resolveCaseData, setResolveCaseData] = useState({
    resolutionSummary: '',
    markedAs: 'Resolved',
    notifyCustomer: true,
    notifyProvider: false
  });
  const [selectedCase, setSelectedCase] = useState<any>(null);

  // Escalate Case Modal
  const [showEscalateCaseModal, setShowEscalateCaseModal] = useState(false);
  const [escalateCaseData, setEscalateCaseData] = useState({
    caseId: '',
    escalationReason: '',
    assignTo: '',
    urgencyLevel: config.forms.defaultEscalationUrgency
  });
  const [selectedCaseToEscalate, setSelectedCaseToEscalate] = useState<any>(null);

  // Publish Page Modal — fields start EMPTY so admin must explicitly enter
  // them (audit 2026-06-04: removed marketing-style placeholder
  // "Quarterly Growth Analysis 2024" which was being submitted as the real
  // page title if admin forgot to edit it).
  const [showPublishPageModal, setShowPublishPageModal] = useState(false);
  const [publishPageData, setPublishPageData] = useState({
    pageTitle: '',
    liveUrlSlug: '',
    deploymentStrategy: 'Publish Now'
  });

  // Confirm Deletion Modal — start blank; admin must select a real record
  // (audit 2026-06-04: removed hardcoded "Virewire_System_Alpha_092" and
  // added recordId so the deletion targets a specific row).
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [deleteFormData, setDeleteFormData] = useState({
    recordType: '',
    recordId: '',
    deleteConfirmation: ''
  });

  // Archive Record Modal — start blank; admin must select a real record
  // (audit 2026-06-04: removed hardcoded "FRI-2024-OMEGA-CODE").
  const [showArchiveRecordModal, setShowArchiveRecordModal] = useState(false);
  const [archiveFormData, setArchiveFormData] = useState({
    recordName: '',
    recordType: 'Active Record'
  });

  // Real data state for dashboard content
  const [jobsRequiringAttention, setJobsRequiringAttention] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [providerAlerts, setProviderAlerts] = useState<any[]>([]);
  const [financialSnapshot, setFinancialSnapshot] = useState<any>(null);
  const [quotesPending, setQuotesPending] = useState<any[]>([]);
  const [customerEscalations, setCustomerEscalations] = useState<any[]>([]);
  const [complianceAlerts, setComplianceAlerts] = useState<any>(null);
  const [payoutQueue, setPayoutQueue] = useState<any>(null);
  const [awaitingReviewStats, setAwaitingReviewStats] = useState<any>(null);
  const [unassignedJobs, setUnassignedJobs] = useState<any[]>([]);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      setLoading(true);
      try {
        const response = await apiService.request('/admin/dashboard/stats', 'GET');
        if (response.success && response.data) {
          setStats({
            activeScale: response.data.activeScale || 0,
            awaitingReview: response.data.awaitingReview || 0,
            variationRequests: response.data.variationRequests || 0,
            overdueActions: response.data.overdueActions || 0,
            complianceWarnings: response.data.complianceWarnings || 0,
            openJobs: response.data.openJobs || 0,
          });
        }

        // Fetch unassigned jobs
        try {
          const unassignedResponse = await apiService.request('/admin/jobs-list?status=unassigned', 'GET');
          if (unassignedResponse.success && Array.isArray(unassignedResponse.data)) {
            setUnassignedJobs(unassignedResponse.data);
          }
        } catch (err) {
          console.error('Failed to fetch unassigned jobs:', err);
        }
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
        // Remove hardcoded fallback - set empty state to alert admin
        setStats({
          activeScale: 0,
          awaitingReview: 0,
          variationRequests: 0,
          overdueActions: 0,
          complianceWarnings: 0,
          openJobs: 0,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardStats();
  }, []);

  // Fetch admin staff for escalation dropdown
  useEffect(() => {
    apiService.request('/admin/staff', 'GET')
      .then(res => { if (res.success && Array.isArray(res.data)) setAdminStaff(res.data); })
      .catch(() => {});
  }, []);

  // Fetch jobs requiring attention
  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await apiService.request('/admin/jobs-list?status=attention', 'GET');
        if (response.success && Array.isArray(response.data)) {
          setJobsRequiringAttention(response.data.slice(0, 2));
        }
      } catch (error) {
        console.error('Error fetching jobs:', error);
      }
    };
    fetchJobs();
  }, []);

  // Fetch recent activity
  useEffect(() => {
    const fetchActivity = async () => {
      try {
        const response = await apiService.request('/admin/recent-activity', 'GET');
        if (response.success && Array.isArray(response.data)) {
          setRecentActivity(response.data.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching activity:', error);
      }
    };
    fetchActivity();
  }, []);

  // Fetch provider alerts
  useEffect(() => {
    const fetchProviders = async () => {
      try {
        const response = await apiService.request('/admin/providers/alerts', 'GET');
        if (response.success && Array.isArray(response.data)) {
          setProviderAlerts(response.data.slice(0, 2));
        }
      } catch (error) {
        console.error('Error fetching provider alerts:', error);
      }
    };
    fetchProviders();
  }, []);

  // Fetch financial snapshot
  useEffect(() => {
    const fetchFinancial = async () => {
      try {
        const response = await apiService.request('/admin/financial-snapshot', 'GET');
        if (response.success && response.data) {
          setFinancialSnapshot(response.data);
        }
      } catch (error) {
        console.error('Error fetching financial snapshot:', error);
      }
    };
    fetchFinancial();
  }, []);

  // Fetch pending quotes
  useEffect(() => {
    const fetchQuotes = async () => {
      try {
        const response = await apiService.request('/admin/quotes-pending', 'GET');
        if (response.success && Array.isArray(response.data)) {
          setQuotesPending(response.data.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching pending quotes:', error);
      }
    };
    fetchQuotes();
  }, []);

  // Fetch customer escalations
  useEffect(() => {
    const fetchEscalations = async () => {
      try {
        const response = await apiService.request('/admin/customer-escalations', 'GET');
        if (response.success && Array.isArray(response.data)) {
          setCustomerEscalations(response.data.slice(0, 2));
        }
      } catch (error) {
        console.error('Error fetching customer escalations:', error);
      }
    };
    fetchEscalations();
  }, []);

  // Fetch compliance alerts
  useEffect(() => {
    const fetchCompliance = async () => {
      try {
        const response = await apiService.request('/admin/compliance-alerts', 'GET');
        if (response.success && response.data) {
          setComplianceAlerts(response.data);
        }
      } catch (error) {
        console.error('Error fetching compliance alerts:', error);
      }
    };
    fetchCompliance();
  }, []);

  // Fetch payout queue
  useEffect(() => {
    const fetchPayouts = async () => {
      try {
        const response = await apiService.request('/admin/payout-queue', 'GET');
        if (response.success && response.data) {
          setPayoutQueue(response.data);
        }
      } catch (error) {
        console.error('Error fetching payout queue:', error);
      }
    };
    fetchPayouts();
  }, []);

  // Fetch awaiting review stats
  useEffect(() => {
    const fetchAwaitingReview = async () => {
      try {
        const response = await apiService.request('/admin/awaiting-review', 'GET');
        if (response.success && response.data) {
          setAwaitingReviewStats(response.data);
        }
      } catch (error) {
        console.error('Error fetching awaiting review stats:', error);
      }
    };
    fetchAwaitingReview();
  }, []);

  // ============================================================
  // BUTTON HANDLER FUNCTIONS FOR ADMIN DASHBOARD
  // All 9 action button handlers with full API integration
  // ============================================================

  // 1. CREATE DISCOUNT HANDLER
  const handleCreateDiscount = async () => {
    if (!discountFormData.discountCode.trim()) {
      toast({ title: 'Validation Error', description: 'Discount code is required', variant: 'destructive' });
      return;
    }
    if (!discountFormData.discountValue) {
      toast({ title: 'Validation Error', description: 'Discount value is required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/create-discount', 'POST', {
        discountCode: discountFormData.discountCode,
        internalTitle: discountFormData.internalTitle || 'Discount - ' + discountFormData.discountCode,
        discountType: discountFormData.discountType,
        discountValue: parseFloat(discountFormData.discountValue || '0'),
        createdBy: localStorage.getItem('adminId') || 'admin',
        createdAt: new Date().toISOString(),
        isActive: true
      });

      if (response.success || response.id) {
        toast({ title: 'Success', description: 'Discount created: ' + discountFormData.discountCode });
        setShowCreateDiscountModal(false);
        setDiscountFormData({
          discountCode: '',
          internalTitle: '',
          discountType: 'Percentage',
          discountValue: ''
        });
      } else {
        toast({ title: 'Error', description: response.message || 'Failed to create discount', variant: 'destructive' });
      }
    } catch (error: any) {
      console.error('Error creating discount:', error);
      toast({ title: 'Error', description: 'Failed to create discount: ' + (error.message || error), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // 2. INVITE ADMIN USER HANDLER
  const handleInviteAdmin = async () => {
    if (!adminFormData.fullName.trim()) {
      toast({ title: 'Validation Error', description: 'Full name is required', variant: 'destructive' });
      return;
    }
    if (!adminFormData.email.includes('@')) {
      toast({ title: 'Validation Error', description: 'Valid email address is required', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/invite-admin-user', 'POST', {
        fullName: adminFormData.fullName,
        email: adminFormData.email,
        phone: adminFormData.phone,
        role: adminFormData.role,
        sendImmediately: adminFormData.sendImmediately,
        invitedBy: localStorage.getItem('adminId') || 'admin',
        inviteTime: new Date().toISOString(),
        inviteToken: Math.random().toString(36).substring(7),
        expiresIn: '7 days'
      });

      if (response.success || response.userId) {
        toast({ title: 'Success', description: 'Invitation sent to: ' + adminFormData.email });
        setShowInviteAdminModal(false);
        setAdminFormData({
          fullName: '',
          email: '',
          phone: '',
          role: 'System Administrator',
          sendImmediately: true
        });
      } else {
        toast({ title: 'Error', description: response.message || 'Failed to send invitation', variant: 'destructive' });
      }
    } catch (error: any) {
      toast({ title: 'Error', description: 'Failed to send invitation', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // 3. CHANGE ADMIN ROLE HANDLER
  const handleChangeAdminRole = async () => {
    if (!selectedAdmin) {
      toast({ title: 'Validation Error', description: 'Please select an admin user', variant: 'destructive' });
      return;
    }
    if (!roleChangeData.newRole) {
      toast({ title: 'Validation Error', description: 'Please select a new role', variant: 'destructive' });
      return;
    }
    if (!roleChangeData.confirmed) {
      toast({ title: 'Validation Error', description: 'Please confirm the role change', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/change-admin-role', 'PUT', {
        adminId: selectedAdmin.id,
        newRole: roleChangeData.newRole,
        addedCapabilities: roleChangeData.addedCapabilities || [],
        removedAccess: roleChangeData.removedAccess || [],
        justification: roleChangeData.justification,
        changedBy: localStorage.getItem('adminId') || 'admin',
        changedAt: new Date().toISOString(),
        previousRole: selectedAdmin.role
      });

      if (response.success) {
        toast({ title: 'Success', description: 'Admin role updated to: ' + roleChangeData.newRole });
        setShowChangeAdminRoleModal(false);
        setSelectedAdmin(null);
      } else {
        toast({ title: 'Error', description: response.message || 'Failed to update role', variant: 'destructive' });
      }
    } catch (error: any) {
      console.error('Error changing admin role:', error);
      toast({ title: 'Error', description: 'Failed to update admin role', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // 4. SUSPEND ADMIN HANDLER
  const handleSuspendAdmin = async () => {
    if (!selectedAdminToSuspend) {
      toast({ title: 'Validation Error', description: 'Please select an admin to suspend', variant: 'destructive' });
      return;
    }
    if (!suspendFormData.suspensionReason) {
      toast({ title: 'Validation Error', description: 'Please provide a suspension reason', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/suspend-admin', 'POST', {
        adminId: selectedAdminToSuspend.id,
        suspensionReason: suspendFormData.suspensionReason,
        auditNotes: suspendFormData.auditNotes,
        suspendedBy: localStorage.getItem('adminId') || 'admin',
        suspendedAt: new Date().toISOString(),
        suspensionDuration: '30 days',
        canAppeal: true,
        appealDeadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
      });

      if (response.success) {
        toast({ title: 'Success', description: 'Admin account suspended' });
        setShowSuspendAdminModal(false);
        setSelectedAdminToSuspend(null);
      } else {
        toast({ title: 'Error', description: response.message || 'Failed to suspend admin', variant: 'destructive' });
      }
    } catch (error: any) {
      console.error('Error suspending admin:', error);
      toast({ title: 'Error', description: 'Failed to suspend admin', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // 5. RESOLVE CASE HANDLER
  const handleResolveCase = async () => {
    if (!selectedCase) {
      toast({ title: 'Validation Error', description: 'Please select a case', variant: 'destructive' });
      return;
    }
    if (!resolveCaseData.resolutionSummary.trim()) {
      toast({ title: 'Validation Error', description: 'Please provide a resolution summary', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/resolve-case', 'PUT', {
        caseId: selectedCase.id,
        resolutionSummary: resolveCaseData.resolutionSummary,
        markedAs: resolveCaseData.markedAs,
        notifyCustomer: resolveCaseData.notifyCustomer,
        notifyProvider: resolveCaseData.notifyProvider,
        resolvedBy: localStorage.getItem('adminId') || 'admin',
        resolvedAt: new Date().toISOString(),
        resolutionTime: selectedCase.createdDate ?
          Math.round((Date.now() - new Date(selectedCase.createdDate).getTime()) / 1000 / 60) + ' minutes'
          : 'unknown'
      });

      if (response.success) {
        alert('✓ Case resolved successfully');
        setShowResolveCaseModal(false);
        setSelectedCase(null);
      } else {
        alert('Error: ' + (response.message || 'Failed to resolve case'));
      }
    } catch (error: any) {
      console.error('Error resolving case:', error);
      alert('Failed to resolve case: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // 6. ESCALATE CASE HANDLER
  const handleEscalateCase = async () => {
    if (!selectedCaseToEscalate) {
      alert('Please select a case');
      return;
    }
    if (!escalateCaseData.escalationReason.trim()) {
      alert('Please provide an escalation reason');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/escalate-case', 'POST', {
        caseId: selectedCaseToEscalate.id,
        escalationReason: escalateCaseData.escalationReason,
        assignTo: escalateCaseData.assignTo,
        urgencyLevel: escalateCaseData.urgencyLevel,
        escalatedBy: localStorage.getItem('adminId') || 'admin',
        escalatedAt: new Date().toISOString(),
        previousAssignment: selectedCaseToEscalate.assignedTo || 'unassigned',
        slaViolation: escalateCaseData.urgencyLevel === 'CRITICAL'
      });

      if (response.success) {
        alert('✓ Case escalated to: ' + escalateCaseData.assignTo);
        setShowEscalateCaseModal(false);
        setSelectedCaseToEscalate(null);
      } else {
        alert('Error: ' + (response.message || 'Failed to escalate case'));
      }
    } catch (error: any) {
      console.error('Error escalating case:', error);
      alert('Failed to escalate case: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // 7. PUBLISH PAGE HANDLER
  const handlePublishPage = async () => {
    if (!publishPageData.pageTitle.trim()) {
      alert('Page title is required');
      return;
    }
    if (!publishPageData.liveUrlSlug.trim()) {
      alert('URL slug is required');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/publish-page', 'POST', {
        pageTitle: publishPageData.pageTitle,
        liveUrlSlug: publishPageData.liveUrlSlug,
        deploymentStrategy: publishPageData.deploymentStrategy,
        publishedBy: localStorage.getItem('adminId') || 'admin',
        publishedAt: new Date().toISOString(),
        environment: 'production',
        isLive: true,
        scheduledFor: null,
        version: '1.0',
        changesSummary: 'Initial publication'
      });

      if (response.success) {
        alert('✓ Page published successfully to: /' + publishPageData.liveUrlSlug);
        setShowPublishPageModal(false);
        setPublishPageData({
          pageTitle: '',
          liveUrlSlug: '',
          deploymentStrategy: 'Publish Now'
        });
      } else {
        alert('Error: ' + (response.message || 'Failed to publish page'));
      }
    } catch (error: any) {
      console.error('Error publishing page:', error);
      alert('Failed to publish page: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // 8. DELETE RECORD HANDLER
  const handleDeleteRecord = async () => {
    if (!deleteFormData.recordType) {
      alert('Please select a record type');
      return;
    }
    if (!deleteFormData.recordId || !deleteFormData.recordId.trim()) {
      alert('Please enter the record ID');
      return;
    }
    if (deleteFormData.deleteConfirmation !== 'DELETE') {
      alert('Type DELETE to confirm');
      return;
    }
    if (!window.confirm(`Permanently delete ${deleteFormData.recordType} #${deleteFormData.recordId}? This cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/delete-record', 'DELETE', {
        recordType: deleteFormData.recordType,
        recordId: deleteFormData.recordId.trim(),
        confirmDeletion: true
      });

      if (response.success) {
        alert('Deletion logged successfully');
        setShowConfirmDeleteModal(false);
        setDeleteFormData({ recordType: '', recordId: '', deleteConfirmation: '' });
      } else {
        alert('Error: ' + (response.message || 'Failed to delete record'));
      }
    } catch (error: any) {
      console.error('Error deleting record:', error);
      alert('Failed to delete record: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  // 9. ARCHIVE RECORD HANDLER
  const handleArchiveRecord = async () => {
    if (!archiveFormData.recordType) {
      alert('Please select archive type');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/archive-record', 'POST', {
        archiveType: archiveFormData.recordType,
        recordId: archiveFormData.recordName || 'all',
        reason: archiveFormData.recordName,
        archivedBy: localStorage.getItem('adminId') || 'admin',
        archivedAt: new Date().toISOString(),
        archiveLocation: '/archive',
        retentionPeriod: '7 years',
        searchable: true,
        compressArchive: true,
        encryptArchive: true
      });

      if (response.success) {
        alert('✓ Record(s) archived successfully');
        setShowArchiveRecordModal(false);
        setArchiveFormData({
          recordName: '',
          recordType: 'Active Record'
        });
      } else {
        alert('Error: ' + (response.message || 'Failed to archive record'));
      }
    } catch (error: any) {
      console.error('Error archiving record:', error);
      alert('Failed to archive record: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center min-h-screen">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <>
      <Helmet>
        <title>Admin Dashboard - Virewire</title>
      </Helmet>

      <AdminLayout>
        <div className={`${layouts.pageContainer}`}>
          <div className={layouts.pageContent}>
            {/* Admin Management Buttons — hidden from the dashboard per
                product request (audit 2026-06-10). The modals, handlers
                and backend endpoints below remain intact so the
                functionality can be re-enabled by removing the `hidden`
                class on this wrapper, or triggered programmatically. */}
            <div className={`hidden flex-wrap ${responsive.cardGap} mb-8 md:mb-12`}>
              <button
                onClick={() => navigate('/admin/service-requests')}
                className={`${componentStyles.buttonPrimary} text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5`}
              >
                📋 Service Requests
              </button>
              <button
                onClick={handleCreateDiscount}
                className={`${componentStyles.buttonPrimary} text-xs sm:text-sm px-3 sm:px-4 py-2 sm:py-2.5`}
              >
                + Create Discount
              </button>
              <button
                onClick={handleInviteAdmin}
                className={`${componentStyles.buttonSecondary} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                + Invite Admin
              </button>
              <button
                onClick={() => setShowChangeAdminRoleModal(true)}
                className={`${componentStyles.buttonSecondary} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                ⚙️ Change Admin Role
              </button>
              <button
                onClick={() => setShowSuspendAdminModal(true)}
                className={`${componentStyles.buttonDanger} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                🚫 Suspend Admin
              </button>
              {/* Resolve/Escalate Case: navigate to the support-disputes page
                  where admin can pick a real open case. Previously these
                  buttons hardcoded a fake CASE-9844 / #SC-8842-X and fired
                  immediately, mutating production state with bogus IDs.
                  (audit 2026-06-04) */}
              <button
                onClick={() => navigate('/admin/support-disputes')}
                className={`${componentStyles.buttonPrimary} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                ✓ Resolve Case
              </button>
              <button
                onClick={() => navigate('/admin/support-disputes')}
                className={`${componentStyles.buttonSecondary} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                ⬆️ Escalate Case
              </button>
              {/* Open the modal so admin fills the form FIRST, then submit.
                  Previously these buttons fired the API handler directly with
                  whatever was in state, which was either hardcoded sample
                  data ("Quarterly Growth Analysis 2024", "Virewire_System
                  _Alpha_092", "FRI-2024-OMEGA-CODE") or — after this audit's
                  defaults cleanup — empty strings, immediately triggering a
                  validation alert. (audit 2026-06-04) */}
              <button
                onClick={() => setShowPublishPageModal(true)}
                className={`${componentStyles.buttonPrimary} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                📄 Publish Page
              </button>
              <button
                onClick={() => setShowConfirmDeleteModal(true)}
                className={`${componentStyles.buttonDanger} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                🗑️ Delete Record
              </button>
              <button
                onClick={() => setShowArchiveRecordModal(true)}
                className={`${componentStyles.buttonSecondary} text-xs sm:text-sm px-3 sm:px-4 py-2`}
              >
                📦 Archive Record
              </button>
            </div>

            {/* Active Header Status */}
            <div className={`mb-6 md:mb-8`}>
              <div className={`flex items-center gap-3 md:gap-4 p-4 md:p-5 bg-gradient-to-r from-green-50 to-emerald-50 border-l-4 border-green-500 rounded-lg shadow-sm hover:shadow-md transition-shadow`}>
                <div className={`flex-shrink-0 flex items-center gap-2`}>
                  <div className={`w-3 h-3 rounded-full bg-green-500 animate-pulse`}></div>
                  <CheckCircle className="w-5 md:w-6 h-5 md:h-6 text-green-600" />
                </div>
                <div className={`flex-1`}>
                  <p className={`text-xs md:text-sm font-semibold text-green-700 uppercase tracking-wide`}>System Status</p>
                  <p className={`text-lg md:text-xl font-bold text-gray-900 mt-1`}>OPERATIONAL</p>
                  <p className={`text-xs md:text-sm text-green-600 mt-0.5`}>All systems running at full capacity</p>
                </div>
                <div className={`flex-shrink-0 flex items-center gap-2 text-gray-400`}>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10.5 1.5H3.75A2.25 2.25 0 001.5 3.75v12.5A2.25 2.25 0 003.75 18.5h12.5a2.25 2.25 0 002.25-2.25V9.5M10.5 1.5l6 6m0 0v-6m0 6h6" strokeWidth="1.5" stroke="currentColor" fill="none" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Status Cards Row */}
            <div className={`grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-3 mb-8 md:mb-12`}>
              <Card className={componentStyles.card}>
                <CardContent className={`p-3 md:p-4`}>
                  <p className="text-xs font-semibold text-gray-600 mb-2">SYSTEM STATUS</p>
                  <p className={`text-lg md:text-2xl font-bold text-green-600`}>Active</p>
                  <p className={`text-xs md:text-sm text-gray-600`}>Platform</p>
                </CardContent>
              </Card>
              <Card className={componentStyles.card}>
                <CardContent className={`p-3 md:p-4`}>
                  <p className="text-xs font-semibold text-gray-600 mb-2">ACTIVE SCALE</p>
                  <p className={`text-lg md:text-2xl font-bold text-gray-900`}>{stats.activeScale.toLocaleString()}</p>
                  <p className={`text-xs md:text-sm text-gray-600`}>Jobs Active</p>
                </CardContent>
              </Card>
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">AWAITING REVIEW</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.awaitingReview}</p>
                </CardContent>
              </Card>
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">PLATFORM REVIEW</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.variationRequests}</p>
                  <p className="text-xs text-gray-600">Variation Requests</p>
                </CardContent>
              </Card>
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">DEADLINES</p>
                  <p className="text-2xl font-bold text-red-600">{stats.overdueActions}</p>
                  <p className="text-xs text-gray-600">Overdue Actions</p>
                </CardContent>
              </Card>
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">COMPLIANCE</p>
                  <p className="text-2xl font-bold text-red-600">{stats.complianceWarnings}</p>
                  <p className="text-xs text-gray-600">Warnings</p>
                </CardContent>
              </Card>
              <Card className="col-span-1">
                <CardContent className="p-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">OPEN JOBS</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.openJobs.toLocaleString()}</p>
                  <p className="text-xs text-gray-600">Variation</p>
                </CardContent>
              </Card>
            </div>

            {/* Main Grid */}
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8`}>
              {/* Left Column */}
              <div className="space-y-4 md:space-y-6">
                {/* Open Jobs */}
                <Card className={componentStyles.card}>
                  <CardHeader>
                    <CardTitle className={`${typography.h4}`}>OPEN JOBS</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className={`text-3xl md:text-4xl font-bold text-gray-900`}>{stats.openJobs.toLocaleString()}</p>
                    <div className="h-1 bg-green-600 rounded-full mt-3 md:mt-4" style={{ width: '100%' }}></div>
                  </CardContent>
                </Card>

                {/* Jobs Requiring Attention */}
                <Card className={componentStyles.card}>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className={`${typography.h5}`}>JOBS REQUIRING ATTENTION</CardTitle>
                    <button className="text-xs text-teal-600 hover:underline">Priority View</button>
                  </CardHeader>
                  <CardContent className="space-y-2 md:space-y-3">
                    {jobsRequiringAttention.length > 0 ? (
                      jobsRequiringAttention.map((job) => (
                        <div key={job.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                          <div>
                            <p className="font-semibold text-gray-900">#{job.id}</p>
                            <p className={`text-xs font-bold text-${job.status === 'urgent' ? 'red' : 'gray'}-600`}>{job.status?.toUpperCase() || 'PENDING'}</p>
                          </div>
                          <Button size="sm" variant="outline">{job.action || 'Review'}</Button>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        <p className="text-sm">No jobs requiring attention</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Awaiting Platform Review */}
                <Card className={componentStyles.card}>
                  <CardHeader>
                    <CardTitle className={`${typography.h5}`}>AWAITING PLATFORM REVIEW</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {awaitingReviewStats?.latest_variation ? (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-xs uppercase font-bold text-gray-600 mb-1">{awaitingReviewStats.latest_variation.id}</p>
                        <p className="text-2xl font-bold text-accent mb-2">£{awaitingReviewStats.latest_variation.amount_gbp || '0.00'}</p>
                        <p className="text-xs text-gray-600 mb-4">{awaitingReviewStats.latest_variation.title}</p>
                        <Button size="sm" variant="outline">Review</Button>
                      </div>
                    ) : (
                      <div className="p-4 bg-gray-50 rounded-lg text-center text-gray-500">
                        <p className="text-sm">No items awaiting review</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Awaiting Review Stats */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 md:gap-4">
                  <Card className={componentStyles.card}>
                    <CardContent className="p-3 md:p-4">
                      <p className="text-xs uppercase font-bold text-gray-600 mb-2">AWAITING REVIEW</p>
                      <p className={`text-xl md:text-2xl font-bold text-gray-900`}>{awaitingReviewStats?.awaiting_review || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className={componentStyles.card}>
                    <CardContent className="p-3 md:p-4">
                      <p className="text-xs uppercase font-bold text-gray-600 mb-2">VARIATION REQUESTS</p>
                      <p className={`text-xl md:text-2xl font-bold text-gray-900`}>{awaitingReviewStats?.variation_requests || 0}</p>
                    </CardContent>
                  </Card>
                  <Card className={componentStyles.card}>
                    <CardContent className="p-3 md:p-4">
                      <p className="text-xs uppercase font-bold text-gray-600 mb-2">OVERDUE ACTIONS</p>
                      <p className={`text-xl md:text-2xl font-bold text-red-600`}>{awaitingReviewStats?.overdue_actions || 0}</p>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Financial Snapshot */}
                <Card className="bg-gray-900 text-white">
                  <CardHeader>
                    <CardTitle className="text-sm font-bold">FINANCIAL SNAPSHOT</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Total Revenue */}
                    <div>
                      <p className="text-xs text-gray-400 mb-1">Total Revenue (Today)</p>
                      <p className="text-3xl font-bold">£{financialSnapshot?.summary?.total_received_gbp || '0.00'}</p>
                    </div>

                    {/* Platform Revenue vs Provider Payouts */}
                    <div className="grid grid-cols-2 gap-3 pt-2">
                      <div className="border-l-2 border-accent pl-3">
                        <p className="text-xs text-gray-400">Platform Revenue (20%)</p>
                        <p className="text-lg font-bold text-accent">£{financialSnapshot?.summary?.platform_revenue_gbp || '0.00'}</p>
                      </div>
                      <div className="border-l-2 border-green-500 pl-3">
                        <p className="text-xs text-gray-400">Hire Payouts (80%)</p>
                        <p className="text-lg font-bold text-green-500">£{financialSnapshot?.summary?.provider_payouts_gbp || '0.00'}</p>
                      </div>
                    </div>

                    {/* Payment Stats */}
                    <div className="border-t border-gray-700 pt-3 mt-3">
                      <p className="text-xs text-gray-400 mb-2">Payment Stats</p>
                      <div className="flex items-center justify-between text-sm">
                        <span>Payments Processed:</span>
                        <span className="font-bold">{financialSnapshot?.summary?.total_payments || 0}</span>
                      </div>
                    </div>

                    {/* Provider Breakdown Preview */}
                    {financialSnapshot?.provider_breakdown && financialSnapshot.provider_breakdown.length > 0 && (
                      <div className="border-t border-gray-700 pt-3 mt-3">
                        <p className="text-xs text-gray-400 mb-2">Top Provider</p>
                        <div className="bg-gray-800 rounded p-2">
                          <p className="text-sm font-bold">{financialSnapshot.provider_breakdown[0].provider_name}</p>
                          <div className="flex justify-between text-xs mt-1">
                            <span className="text-gray-400">Earned:</span>
                            <span className="text-accent">£{financialSnapshot.provider_breakdown[0].total_received_gbp}</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-400">Wallet:</span>
                            <span className="text-green-500">£{financialSnapshot.provider_breakdown[0].wallet_balance_gbp}</span>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* View Full Report Button */}
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="w-full mt-2 text-gray-900 hover:text-gray-900"
                      onClick={() => navigate('/admin/financial-dashboard')}
                    >
                      View Full Report →
                    </Button>
                  </CardContent>
                </Card>

                {/* Quotes Pending */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-bold">QUOTES PENDING</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {quotesPending.length > 0 ? (
                      quotesPending.map((quote) => (
                        <div key={quote.id} className="p-3 border border-gray-200 rounded-lg">
                          <p className="text-xs uppercase font-bold text-gray-600 mb-1">{quote.id}</p>
                          <p className="text-xl font-bold text-gray-900">£{quote.amount_gbp}</p>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/admin/billing?quoteId=${quote.id}`)}
                            className="mt-2"
                          >
                            Close Approval
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="p-4 text-center text-gray-500">
                        <p className="text-sm">No pending quotes</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Customer Escalations */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-bold">CUSTOMER ESCALATIONS</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {customerEscalations.length > 0 ? (
                      customerEscalations.map((escalation) => (
                        <div key={escalation.id} className="p-3 border-l-4 border-red-500 bg-red-50 rounded">
                          <p className="text-xs uppercase font-bold text-red-600 mb-1">{escalation.urgency}</p>
                          <p className="font-semibold text-gray-900">{escalation.id}: {escalation.title}</p>
                          <p className="text-xs text-gray-600 mt-1">{escalation.customer}</p>
                          <Button
                            size="sm"
                            onClick={() => navigate(`/admin/support-disputes/${escalation.case_id || escalation.id?.replace(/^#?CASE-/, '')}`)}
                            className="mt-2 w-full bg-gray-900 hover:bg-gray-800"
                          >
                            View Ticket
                          </Button>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500">
                        <p className="text-sm">No escalations</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Compliance Alerts */}
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-base font-bold">COMPLIANCE ALERTS</CardTitle>
                    <span className="text-xs font-bold text-red-600">{complianceAlerts?.total_alerts || 0} Alerts</span>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {complianceAlerts?.alerts && complianceAlerts.alerts.length > 0 ? (
                      complianceAlerts.alerts.slice(0, 2).map((alert: any, idx: number) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className="w-8 h-8 bg-orange-100 rounded flex items-center justify-center flex-shrink-0 text-orange-600">{alert.icon}</div>
                          <div>
                            <p className="font-semibold text-gray-900">{alert.provider}</p>
                            <p className="text-xs text-gray-600">{alert.issue}</p>
                            <Button
                              size="sm"
                              variant="link"
                              onClick={() => navigate(`/admin/provider-documents?providerId=${alert.id || ''}`)}
                              className="text-xs p-0 h-auto"
                            >
                              Review Compliance File
                            </Button>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500">
                        <p className="text-sm">No compliance alerts</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Unassigned Jobs */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-bold">UNASSIGNED</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {unassignedJobs && unassignedJobs.length > 0 ? (
                      <div className="space-y-3">
                        {unassignedJobs.slice(0, 3).map((job: any) => (
                          <div key={job.id} className="border-b pb-3 last:border-b-0 last:pb-0">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-semibold text-sm">#{job.id || 'N/A'}</p>
                              <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">PENDING</span>
                            </div>
                            <p className="text-xs text-gray-600 mb-2">{job.service_name || 'Service'}</p>
                            <Button
                              size="sm"
                              onClick={() => navigate(`/admin/bookings?bookingId=${job.id}&action=assign`)}
                              className="w-full mt-2 bg-accent hover:bg-accent/90 text-xs"
                            >
                              Assign Provider
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-gray-500">No unassigned jobs</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Payout Queue */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base font-bold">PAYOUT QUEUE</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-xs uppercase font-bold text-gray-600 mb-2">TOTAL DISBURSING</p>
                      <p className="text-2xl font-bold text-gray-900">£{payoutQueue?.total_disbursing_gbp || '0.0k'}</p>
                    </div>
                    <div className="space-y-2">
                      {payoutQueue?.payouts && payoutQueue.payouts.length > 0 ? (
                        payoutQueue.payouts.map((payout: any) => (
                          <div key={payout.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <span className="text-xs font-semibold">ID: {payout.id}</span>
                            <Button
                              size="sm"
                              variant="default"
                              onClick={async () => {
                                if (!window.confirm(`Release payout #${payout.id}?`)) return;
                                try {
                                  const res: any = await apiService.request(`/admin/payouts/${payout.id}/release`, 'POST', {});
                                  if (res?.success) {
                                    alert(res.message || 'Payout released');
                                    // Optimistic remove
                                    setPayoutQueue((prev: any) => prev ? {
                                      ...prev,
                                      payouts: (prev.payouts || []).filter((p: any) => p.id !== payout.id)
                                    } : prev);
                                  } else {
                                    alert('Error: ' + (res?.message || 'Failed to release payout'));
                                  }
                                } catch (e: any) {
                                  alert('Failed: ' + (e?.message || 'Network error'));
                                }
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              RELEASE
                            </Button>
                          </div>
                        ))
                      ) : (
                        <div className="text-center text-gray-500">
                          <p className="text-sm">No pending payouts</p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Quality Assurance Review */}
                <Card className="bg-gradient-to-br from-teal-50 to-cyan-50">
                  <CardHeader>
                    <CardTitle className="text-base font-bold text-teal-900">QUALITY ASSURANCE</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <p className="text-xs uppercase font-bold text-teal-700 mb-2">Awaiting QA Review</p>
                      <p className="text-3xl font-bold text-teal-900">14</p>
                      <p className="text-xs text-teal-700 mt-1">Jobs pending QA verification</p>
                    </div>
                    <Button 
                      onClick={() => navigate('/admin/qa')}
                      className="w-full bg-teal-600 hover:bg-teal-700 text-white"
                    >
                      Open QA Dashboard →
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-2 gap-8 mt-8">
              {/* Recent Platform Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-bold">RECENT PLATFORM ACTIVITY</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {recentActivity.length > 0 ? (
                    recentActivity.map((activity, idx) => (
                      <div key={idx} className="flex gap-3 text-sm">
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0 text-blue-600">
                          {activity.icon || '📋'}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{activity.action || 'Activity'}</p>
                          <p className="text-xs text-gray-600">{activity.details || 'Update'}</p>
                          <p className="text-xs text-gray-500 mt-1">{activity.time || 'Recently'}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-600">No recent activity</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Provider Performance Alerts */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base font-bold">PROVIDER PERFORMANCE ALERTS</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {providerAlerts.length > 0 ? (
                    providerAlerts.map((provider, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg">
                        <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold">
                          {provider.name ? provider.name[0] : 'P'}
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{provider.name || 'Provider'}</p>
                          <p className="text-xs text-gray-600">{provider.rating || 'Rating'}</p>
                        </div>
                        <span className="text-xs font-semibold text-gray-700">{provider.status || 'Status'}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-sm text-gray-600">No provider alerts</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>

        {/* Create Discount Modal */}
        {showCreateDiscountModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold">Create Discount</h2>
                <button
                  onClick={() => setShowCreateDiscountModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const response = await apiService.post('/admin/discounts/create', discountFormData);
                    if (response.success) {
                      alert('Discount created successfully');
                      setDiscountFormData({
                        discountCode: '',
                        internalTitle: '',
                        discountType: 'Percentage',
                        discountValue: ''
                      });
                      setShowCreateDiscountModal(false);
                      // Refresh stats
                      const statsResponse = await apiService.request('/admin/dashboard/stats', 'GET');
                      if (statsResponse.success) {
                        setStats(statsResponse.data);
                      }
                    }
                  } catch (error) {
                    console.error('Error creating discount:', error);
                    alert('Failed to create discount');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    DISCOUNT CODE
                  </label>
                  <input
                    type="text"
                    value={discountFormData.discountCode}
                    onChange={(e) =>
                      setDiscountFormData({ ...discountFormData, discountCode: e.target.value })
                    }
                    placeholder="E.g. SUMMER_24"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    INTERNAL TITLE
                  </label>
                  <input
                    type="text"
                    value={discountFormData.internalTitle}
                    onChange={(e) =>
                      setDiscountFormData({ ...discountFormData, internalTitle: e.target.value })
                    }
                    placeholder="E.g. Seasonal Promo"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    DISCOUNT TYPE
                  </label>
                  <div className="flex gap-3">
                    {['Percentage', 'Fixed Amount', 'Free Shipping'].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() =>
                          setDiscountFormData({ ...discountFormData, discountType: type })
                        }
                        className={`px-3 py-2 rounded-lg text-xs font-semibold transition-colors ${
                          discountFormData.discountType === type
                            ? 'bg-teal-600 text-white'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {type}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    DISCOUNT VALUE
                  </label>
                  <input
                    type="number"
                    value={discountFormData.discountValue}
                    onChange={(e) =>
                      setDiscountFormData({ ...discountFormData, discountValue: e.target.value })
                    }
                    placeholder="Enter amount or percentage"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <p className="text-xs text-gray-500 bg-gray-50 p-3 rounded">
                  Eligible services will be visible on the next screen. Select which services this discount applies to.
                </p>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowCreateDiscountModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-semibold"
                  >
                    Next Step →
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Invite Admin Modal */}
        {showInviteAdminModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">Invite Admin</h2>
                <button
                  onClick={() => setShowInviteAdminModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Send a new administrator an email with a secure onboarding link. They will create a password and set up two-factor authentication.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const response = await apiService.post('/admin/admins/invite', adminFormData);
                    if (response.success) {
                      alert('Admin invitation sent successfully');
                      setAdminFormData({
                        fullName: '',
                        email: '',
                        phone: '',
                        role: 'System Administrator',
                        sendImmediately: true
                      });
                      setShowInviteAdminModal(false);
                    }
                  } catch (error) {
                    console.error('Error inviting admin:', error);
                    alert('Failed to send invitation');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    FULL NAME
                  </label>
                  <input
                    type="text"
                    value={adminFormData.fullName}
                    onChange={(e) =>
                      setAdminFormData({ ...adminFormData, fullName: e.target.value })
                    }
                    placeholder="E.g. Adrian Thomas"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    EMAIL ADDRESS
                  </label>
                  <input
                    type="email"
                    value={adminFormData.email}
                    onChange={(e) =>
                      setAdminFormData({ ...adminFormData, email: e.target.value })
                    }
                    placeholder="admin@company.com"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    PHONE NUMBER
                  </label>
                  <input
                    type="tel"
                    value={adminFormData.phone}
                    onChange={(e) =>
                      setAdminFormData({ ...adminFormData, phone: e.target.value })
                    }
                    placeholder="+44 (0) 20 1234 5678"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    ADMINISTRATIVE ROLE
                  </label>
                  <select
                    value={adminFormData.role}
                    onChange={(e) =>
                      setAdminFormData({ ...adminFormData, role: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>System Administrator</option>
                    <option>Finance Operations Lead</option>
                    <option>Compliance Officer</option>
                    <option>Support Manager</option>
                  </select>
                </div>

                <div className="bg-gray-50 p-3 rounded text-xs text-gray-600">
                  Administrators can manage discounts, approve variations, monitor compliance documents, and manage team members.
                </div>

                <label className="flex items-center gap-3 cursor-pointer py-2">
                  <input
                    type="checkbox"
                    checked={adminFormData.sendImmediately}
                    onChange={(e) =>
                      setAdminFormData({ ...adminFormData, sendImmediately: e.target.checked })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-green-600"
                  />
                  <div>
                    <span className="text-sm font-semibold text-gray-700">Send invitation immediately</span>
                    <p className="text-xs text-gray-500">Invite link will be valid for 7 days</p>
                  </div>
                </label>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowInviteAdminModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    Send Invitation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Change Admin Role Modal */}
        {showChangeAdminRoleModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md max-h-screen overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">Change Admin Role</h2>
                <button
                  onClick={() => setShowChangeAdminRoleModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!selectedAdmin) return;
                  try {
                    const response = await apiService.post(`/admin/admins/${selectedAdmin.id}/role-change`, {
                      newRole: roleChangeData.newRole,
                      justification: roleChangeData.justification,
                      confirmed: roleChangeData.confirmed
                    });
                    if (response.success) {
                      alert('Admin role updated successfully');
                      setShowChangeAdminRoleModal(false);
                      setRoleChangeData({
                        newRole: 'Finance Operations Lead',
                        addedCapabilities: [],
                        removedAccess: [],
                        justification: '',
                        confirmed: false
                      });
                    }
                  } catch (error) {
                    console.error('Error updating admin role:', error);
                    alert('Failed to update admin role');
                  }
                }}
                className="space-y-4"
              >
                {/* Admin Selection */}
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Select Admin</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={selectedAdmin?.id || ''}
                    onChange={e => {
                      const s = adminStaff.find(x => String(x.id) === e.target.value);
                      setSelectedAdmin(s ? { id: s.id, name: s.name, role: s.role, currentRole: s.role } : null);
                    }}
                    required
                  >
                    <option value="">-- Select admin user --</option>
                    {adminStaff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                  </select>
                </div>

                {/* Admin Info */}
                {selectedAdmin && <div className="flex items-center gap-3 mb-2 p-3 bg-gray-50 rounded">
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold">
                    {selectedAdmin.name?.[0] || '?'}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedAdmin.name}</p>
                    <p className="text-xs text-gray-500">{selectedAdmin.email || ''}</p>
                  </div>
                </div>}

                {selectedAdmin && <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    CURRENT ROLE
                  </label>
                  <div className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50 text-gray-700">
                    {selectedAdmin.currentRole || selectedAdmin.role}
                  </div>
                </div>}

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    NEW ASSIGNMENT
                  </label>
                  <select
                    value={roleChangeData.newRole}
                    onChange={(e) =>
                      setRoleChangeData({ ...roleChangeData, newRole: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>System Administrator</option>
                    <option>Finance Operations Lead</option>
                    <option>Compliance Officer</option>
                    <option>Support Manager</option>
                    <option>Audit Manager</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">
                    PERMISSION IMPACT SUMMARY
                  </label>
                  <div className="space-y-3">
                    <div className="border border-green-200 bg-green-50 p-3 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="added"
                          defaultChecked
                          className="w-4 h-4 rounded border-gray-300 text-green-600"
                        />
                        <label htmlFor="added" className="text-sm font-semibold text-gray-700">
                          Added Capabilities
                        </label>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">
                        Budget Analysis & Reporting, Financial Controls, Team Management & Training
                      </p>
                    </div>
                    <div className="border border-red-200 bg-red-50 p-3 rounded">
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="removed"
                          defaultChecked
                          className="w-4 h-4 rounded border-gray-300 text-red-600"
                        />
                        <label htmlFor="removed" className="text-sm font-semibold text-gray-700">
                          Removed Access
                        </label>
                      </div>
                      <p className="text-xs text-gray-600 ml-6">
                        Budget Management & Team Configuration Tools
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    JUSTIFICATION FOR CHANGE
                  </label>
                  <textarea
                    value={roleChangeData.justification}
                    onChange={(e) =>
                      setRoleChangeData({ ...roleChangeData, justification: e.target.value })
                    }
                    placeholder="Explain the reason for this role change..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  />
                </div>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={roleChangeData.confirmed}
                    onChange={(e) =>
                      setRoleChangeData({ ...roleChangeData, confirmed: e.target.checked })
                    }
                    className="w-5 h-5 rounded border-gray-300 text-blue-600 mt-1"
                  />
                  <span className="text-xs text-gray-600">
                    I confirm this role change is authorized by Department Oversight
                  </span>
                </label>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowChangeAdminRoleModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={!roleChangeData.confirmed}
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Update Role
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Suspend Admin Account Modal */}
        {showSuspendAdminModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold">Suspend Admin Account</h2>
                <button
                  onClick={() => setShowSuspendAdminModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Security Violation Header */}
              <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded">
                <p className="text-xs font-bold text-red-600 uppercase tracking-widest mb-2">
                  🔐 SECURITY VIOLATION PROTOCOL
                </p>
                <div className="bg-red-100 border border-red-300 rounded p-3">
                  <div className="flex items-start gap-2">
                    <span className="text-red-600 text-lg">⚠️</span>
                    <div>
                      <p className="text-xs font-bold text-red-700 mb-1">Account Suspension Warning</p>
                      <p className="text-xs text-red-600">
                        Suspending this account will immediately revoke all platform access, terminate active sessions, and freeze all pending operational approvals. This action is logged for audit purposes.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!selectedAdminToSuspend) { alert('Please select an admin to suspend'); return; }
                  try {
                    const response = await apiService.post(`/admin/admins/${selectedAdminToSuspend.id}/suspend`, suspendFormData);
                    if (response.success) {
                      alert('Admin account suspended successfully');
                      setShowSuspendAdminModal(false);
                      setSelectedAdminToSuspend(null);
                      setSuspendFormData({ suspensionReason: '', auditNotes: '' });
                    }
                  } catch (error) {
                    console.error('Error suspending admin:', error);
                    alert('Failed to suspend admin account');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">SELECT ADMIN TO SUSPEND</label>
                  <select
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={selectedAdminToSuspend?.id || ''}
                    onChange={e => {
                      const s = adminStaff.find(x => String(x.id) === e.target.value);
                      setSelectedAdminToSuspend(s ? { id: s.id, name: s.name, adminId: String(s.id) } : null);
                    }}
                    required
                  >
                    <option value="">-- Select admin user --</option>
                    {adminStaff.map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    SUSPENSION REASON
                  </label>
                  <select
                    value={suspendFormData.suspensionReason}
                    onChange={(e) =>
                      setSuspendFormData({ ...suspendFormData, suspensionReason: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>Unconfirmed Security Clearance Escalation</option>
                    <option>Unauthorized Access Attempt</option>
                    <option>Compliance Violation</option>
                    <option>Malicious Activity Detected</option>
                    <option>Account Compromise</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    AUDIT NOTES
                  </label>
                  <textarea
                    value={suspendFormData.auditNotes}
                    onChange={(e) =>
                      setSuspendFormData({ ...suspendFormData, auditNotes: e.target.value })
                    }
                    placeholder="Provide detailed context for the audit trail..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[80px]"
                  />
                </div>

                {/* Admin Info */}
                <div className="flex items-center gap-3 p-3 bg-gray-50 rounded mt-4">
                  <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center text-white font-bold">
                    {selectedAdminToSuspend.name[0]}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{selectedAdminToSuspend.name}</p>
                    <p className="text-xs text-gray-500">{selectedAdminToSuspend.adminId}</p>
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowSuspendAdminModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Suspend Account
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Resolve Case Modal */}
        {showResolveCaseModal && selectedCase && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded mb-2">
                    RESOLUTION PROTOCOL
                  </span>
                  <h2 className="text-lg font-bold">Resolve Case</h2>
                </div>
                <button
                  onClick={() => setShowResolveCaseModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!selectedCase) return;
                  try {
                    const response = await apiService.post(`/admin/cases/${selectedCase.id}/resolve`, resolveCaseData);
                    if (response.success) {
                      alert('Case marked as resolved');
                      setShowResolveCaseModal(false);
                      setResolveCaseData({
                        resolutionSummary: '',
                        markedAs: 'Resolved',
                        notifyCustomer: true,
                        notifyProvider: false
                      });
                    }
                  } catch (error) {
                    console.error('Error resolving case:', error);
                    alert('Failed to resolve case');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    RESOLUTION SUMMARY
                  </label>
                  <textarea
                    value={resolveCaseData.resolutionSummary}
                    onChange={(e) =>
                      setResolveCaseData({ ...resolveCaseData, resolutionSummary: e.target.value })
                    }
                    placeholder="Detail the final resolution and actions taken..."
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm min-h-[100px] bg-gray-50"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    MARKED AS...
                  </label>
                  <select
                    value={resolveCaseData.markedAs}
                    onChange={(e) =>
                      setResolveCaseData({ ...resolveCaseData, markedAs: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>Resolved</option>
                    <option>Partially Resolved</option>
                    <option>Escalated</option>
                    <option>Closed</option>
                  </select>
                </div>

                {/* Stakeholder Notifications */}
                <div className="bg-gray-50 p-4 rounded border border-gray-200">
                  <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">
                    STAKEHOLDER NOTIFICATIONS
                  </p>
                  <div className="space-y-3">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resolveCaseData.notifyCustomer}
                        onChange={(e) =>
                          setResolveCaseData({ ...resolveCaseData, notifyCustomer: e.target.checked })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-green-600"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-gray-700">Notify customer</span>
                        <p className="text-xs text-gray-500">Send resolution update and next steps</p>
                      </div>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={resolveCaseData.notifyProvider}
                        onChange={(e) =>
                          setResolveCaseData({ ...resolveCaseData, notifyProvider: e.target.checked })
                        }
                        className="w-5 h-5 rounded border-gray-300 text-gray-400"
                      />
                      <div className="flex-1">
                        <span className="text-sm font-semibold text-gray-700">Notify provider</span>
                        <p className="text-xs text-gray-500">Alert service provider to case closure</p>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Close Checklist */}
                <div className="bg-yellow-50 border border-yellow-200 p-3 rounded">
                  <p className="text-xs font-bold text-yellow-700 mb-1">⚠️ Close checklist</p>
                  <p className="text-xs text-yellow-600">
                    Ensure all case requirements have been met and the case can be safely closed permanently approved.
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowResolveCaseModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    Mark Resolved
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Escalate Case Modal */}
        {showEscalateCaseModal && selectedCaseToEscalate && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <span className="inline-block px-3 py-1 bg-red-100 text-red-700 text-xs font-bold rounded mb-2">
                    ESCALATION REQUEST
                  </span>
                  <h2 className="text-lg font-bold">Escalate Case {escalateCaseData.caseId}</h2>
                </div>
                <button
                  onClick={() => setShowEscalateCaseModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Transfer ownership and urgently priority level immediately.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (!selectedCaseToEscalate) return;
                  try {
                    const response = await apiService.post(`/admin/cases/${selectedCaseToEscalate.id}/escalate`, escalateCaseData);
                    if (response.success) {
                      alert('Case escalated successfully');
                      setShowEscalateCaseModal(false);
                      setEscalateCaseData({ caseId: '', escalationReason: '', assignTo: '', urgencyLevel: 'HIGH' });
                    }
                  } catch (error) {
                    console.error('Error escalating case:', error);
                    alert('Failed to escalate case');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    ESCALATION REASON
                  </label>
                  <select
                    value={escalateCaseData.escalationReason}
                    onChange={(e) =>
                      setEscalateCaseData({ ...escalateCaseData, escalationReason: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>SLA Violation detected</option>
                    <option>Customer Dissatisfaction</option>
                    <option>Compliance Issue</option>
                    <option>Financial Impact</option>
                    <option>Security Concern</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    ASSIGN TO
                  </label>
                  <select
                    value={escalateCaseData.assignTo}
                    onChange={(e) =>
                      setEscalateCaseData({ ...escalateCaseData, assignTo: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    {adminStaff.length > 0 ? (
                      adminStaff.map(s => (
                        <option key={s.id} value={`${s.name} (${s.role})`}>
                          {s.name} ({s.role})
                        </option>
                      ))
                    ) : (
                      <option value="">Loading staff...</option>
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">
                    URGENCY LEVEL
                  </label>
                  <div className="space-y-2">
                    {['Medium', 'High', 'CRITICAL'].map((level) => (
                      <label key={level} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="urgency"
                          value={level}
                          checked={escalateCaseData.urgencyLevel === level}
                          onChange={(e) =>
                            setEscalateCaseData({ ...escalateCaseData, urgencyLevel: e.target.value })
                          }
                          className="w-4 h-4 text-orange-600"
                        />
                        <span className={`text-sm font-semibold ${
                          level === 'CRITICAL' ? 'text-red-600' : 'text-gray-700'
                        }`}>
                          {level}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Critical Warning */}
                {escalateCaseData.urgencyLevel === 'CRITICAL' && (
                  <div className="bg-yellow-50 border border-yellow-300 p-3 rounded">
                    <p className="text-xs font-bold text-yellow-700 mb-1">⚠️ Escalating this case will trigger a real-time ICTO notification.</p>
                    <p className="text-xs text-yellow-600">This action is logged and cannot be undone immediately.</p>
                  </div>
                )}

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowEscalateCaseModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold"
                  >
                    Escalate Case
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Publish Page Modal */}
        {showPublishPageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📄</span>
                  <h2 className="text-lg font-bold">Publish Page</h2>
                </div>
                <button
                  onClick={() => setShowPublishPageModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                Review the architectural integrity and deployment schedule before pushing to the live environment.
              </p>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const response = await apiService.post('/admin/pages/publish', publishPageData);
                    if (response.success) {
                      alert('Page published successfully');
                      setPublishPageData({
                        pageTitle: '',
                        liveUrlSlug: '',
                        deploymentStrategy: 'Publish Now'
                      });
                      setShowPublishPageModal(false);
                    }
                  } catch (error) {
                    console.error('Error publishing page:', error);
                    alert('Failed to publish page');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    PAGE TITLE
                  </label>
                  <input
                    type="text"
                    value={publishPageData.pageTitle}
                    onChange={(e) =>
                      setPublishPageData({ ...publishPageData, pageTitle: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    LIVE URL SLUG
                  </label>
                  <input
                    type="text"
                    value={publishPageData.liveUrlSlug}
                    onChange={(e) =>
                      setPublishPageData({ ...publishPageData, liveUrlSlug: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-gray-50"
                    readOnly
                  />
                </div>

                {/* Critical Warning */}
                <div className="border-l-4 border-red-500 bg-red-50 p-3 rounded">
                  <p className="text-xs font-bold text-red-700 mb-2">⚠️ Critical Update:</p>
                  <p className="text-xs text-red-600">
                    This action will redeploy and validate version at this URL, and notify all subscribed providers. This cannot be undone once the cache is purged.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-3">
                    DEPLOYMENT STRATEGY
                  </label>
                  <div className="space-y-2">
                    {['Publish Now', 'Schedule'].map((strategy) => (
                      <label key={strategy} className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="radio"
                          name="deployment"
                          value={strategy}
                          checked={publishPageData.deploymentStrategy === strategy}
                          onChange={(e) =>
                            setPublishPageData({ ...publishPageData, deploymentStrategy: e.target.value })
                          }
                          className="w-4 h-4"
                        />
                        <div>
                          <span className="text-sm font-semibold text-gray-700">{strategy}</span>
                          <p className="text-xs text-gray-500">
                            {strategy === 'Publish Now' ? 'Immediate Push' : 'Set deployment time'}
                          </p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowPublishPageModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    CANCEL
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold"
                  >
                    PUBLISH NOW →
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Confirm Deletion Modal */}
        {showConfirmDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="text-red-600 text-2xl mb-2">⚠️</div>
                  <div>
                    <h2 className="text-lg font-bold">Confirm Deletion</h2>
                    <p className="text-xs font-bold text-red-600 uppercase tracking-widest">
                      THIS ACTION WILL BE RECORDED
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setShowConfirmDeleteModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleDeleteRecord();
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    Record Type
                  </label>
                  <select
                    value={deleteFormData.recordType}
                    onChange={(e) =>
                      setDeleteFormData({ ...deleteFormData, recordType: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option value="">— Select record type —</option>
                    <option value="booking">Booking / Job</option>
                    <option value="quote">Quote</option>
                    <option value="notice">Notice</option>
                    <option value="discount">Discount Code</option>
                    <option value="support_ticket">Support Ticket</option>
                    <option value="expiry_alert">Expiry Alert</option>
                    <option value="user">User</option>
                    <option value="property">Property</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    Record ID
                  </label>
                  <input
                    type="text"
                    value={deleteFormData.recordId}
                    onChange={(e) =>
                      setDeleteFormData({ ...deleteFormData, recordId: e.target.value })
                    }
                    placeholder="e.g. 1775939529"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div className="bg-red-50 border border-red-200 p-4 rounded space-y-3">
                  <p className="text-sm text-red-700 font-semibold">
                    You are about to permanently purge this record from the knowledge database. This action is irreversible.
                  </p>
                  <p className="text-xs text-red-600">
                    Associated financial logs and certificates will be revoked immediately.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-bold text-red-600 uppercase tracking-widest mb-2">
                    TYPE DELETE TO CONFIRM
                  </label>
                  <input
                    type="text"
                    value={deleteFormData.deleteConfirmation}
                    onChange={(e) =>
                      setDeleteFormData({ ...deleteFormData, deleteConfirmation: e.target.value })
                    }
                    placeholder="Type DELETE..."
                    className="w-full border border-red-300 rounded-lg px-3 py-2 text-sm text-red-600 placeholder-red-400"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowConfirmDeleteModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel Action
                  </button>
                  <button
                    type="submit"
                    disabled={deleteFormData.deleteConfirmation !== 'DELETE'}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    Delete Permanently
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Archive Record Modal */}
        {showArchiveRecordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📦</span>
                  <h2 className="text-lg font-bold">Archive Record</h2>
                </div>
                <button
                  onClick={() => setShowArchiveRecordModal(false)}
                  className="text-gray-400 hover:text-gray-600 text-xl"
                >
                  ✕
                </button>
              </div>

              <form
                onSubmit={async (e) => {
                  e.preventDefault();
                  try {
                    const response = await apiService.post(`/admin/records/${archiveFormData.recordName}/archive`, archiveFormData);
                    if (response.success) {
                      alert('Record archived successfully');
                      setArchiveFormData({
                        recordName: '',
                        recordType: 'Active Record'
                      });
                      setShowArchiveRecordModal(false);
                    }
                  } catch (error) {
                    console.error('Error archiving record:', error);
                    alert('Failed to archive record');
                  }
                }}
                className="space-y-4"
              >
                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    RECORD NAME
                  </label>
                  <input
                    type="text"
                    value={archiveFormData.recordName}
                    onChange={(e) =>
                      setArchiveFormData({ ...archiveFormData, recordName: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                    RECORD TYPE
                  </label>
                  <select
                    value={archiveFormData.recordType}
                    onChange={(e) =>
                      setArchiveFormData({ ...archiveFormData, recordType: e.target.value })
                    }
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  >
                    <option>Active Record</option>
                    <option>Archived Record</option>
                    <option>System Record</option>
                    <option>Compliance Record</option>
                  </select>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Archiving this record will move it to the 'Cloud Vault' where it will remain inaccessible within active systems.
                </p>

                {/* Warning Box */}
                <div className="border-l-4 border-yellow-400 bg-yellow-50 p-3 rounded">
                  <p className="text-xs text-yellow-700 font-semibold">
                    ⚠️ This action can be reversed by an administrator with Vault Management permissions.
                  </p>
                </div>

                <div className="flex gap-3 pt-4 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={() => setShowArchiveRecordModal(false)}
                    className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors font-semibold"
                  >
                    Archive Record
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminDashboardPage;